<template>
  <v-container fluid class="pa-md-12">
    <v-row wrap>
      <v-col cols="12">
        <v-card class="pa-5 px-md-12 py-md-6">
          <v-row v-if="loading">
            <v-col class="text-center">
              <v-progress-circular
                indeterminate
                color="progress"
                size="15"
                width="2"
              ></v-progress-circular>
            </v-col>
          </v-row>
          <v-row v-else wrap>
            <v-col cols="12">
              <div
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="0"
                class="font-weight-bold"
              >
                Profile Page
              </div>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12">
              <div
                class="text-h6"
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="25"
              >
                Basic Details
              </div>
            </v-col>
            <v-col cols="12">
              <div
                class="text-caption"
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="50"
              >
                {{ userStore.basic.fName }}
                {{ userStore.basic.lName }}
              </div>
              <div
                class="text-caption"
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="75"
              >
                {{ userStore.basic.email }}
              </div>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12">
              <div
                class="text-h6"
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="100"
              >
                Subscription Details
              </div>
            </v-col>

            <template v-for="(name, id) in productNames" :key="id">
              <v-col cols="12" v-if="paymentStore.isSubscribed?.[id]?.val">
                <div
                  class="text-caption"
                  v-motion
                  :initial="{ opacity: 0, x: 10 }"
                  :enter="{ opacity: 1, x: 0, scale: 1 }"
                  :delay="125"
                >
                  <strong>{{ name }}:</strong> Active until
                  <strong>{{
                    easyToReadDate(
                      paymentStore.isSubscribed?.[id]?.endDate,
                      true,
                    )
                  }}</strong>
                </div>
              </v-col>
            </template>
            <v-col cols="12">
              <v-alert
                type="info"
                variant="tonal"
                icon="mdi-alert-circle-outline"
              >
                <template #text>
                  To cancel a recurring subscription, login at
                  <a
                    href="https://eye.academy/account"
                    target="_blank"
                    rel="noopener"
                  >
                    eye.academy/account </a
                  >, click subscriptions, select your product, and cancel the
                  subscription.
                </template>
              </v-alert>
            </v-col>
            <v-col v-if="!hasAnySubscription" cols="12">
              <div
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="150"
                class="text-caption"
              >
                You are currently not subscribed to any banks. You can create 1
                session to try out our platform.
                <br />
                You can purchase a subscription on the main
                <a href="https://eye.academy" target="_blank"
                  >Eye Academy website</a
                >.
              </div>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12">
              <div
                class="text-h6"
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="200"
              >
                Danger Zone
              </div>
            </v-col>
            <v-col
              cols="12"
              v-motion
              :initial="{ opacity: 0, x: 10 }"
              :enter="{ opacity: 1, x: 0, scale: 1 }"
              :delay="200"
            >
              <div>
                <v-alert
                  text="These actions are irreversible."
                  type="error"
                  variant="tonal"
                  icon="mdi-alert-circle-outline"
                >
                </v-alert>
              </div>
              <v-row class="mt-4">
                <v-col
                  v-for="(name, id) in resetableProducts"
                  :key="id"
                  cols="12"
                >
                  <v-btn
                    @click="openResetModal(id)"
                    color="error"
                    rounded="md"
                    variant="flat"
                    size="small"
                  >
                    Reset {{ name }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card>
        <v-dialog
          v-model="openConfirmModal"
          width="500"
          persistent
          min-height="400"
        >
          <v-card title="Confirmation">
            <v-card-text>
              This will reset your progress and flags for {{ selectedProductName }}.
              <br /><br />
              Your existing sessions and comments will remain available to view.
              Start a new session after resetting.
              <br /><br />
              Are you sure you want to continue?
            </v-card-text>
            <v-divider class="my-3"></v-divider>

            <v-card-actions>
              <v-row>
                <v-col cols="9">
                  <v-btn
                    variant="flat"
                    color="primary"
                    block
                    @click="openConfirmModal = false"
                    >Close</v-btn
                  >
                </v-col>
                <v-col cols="3">
                  <v-btn
                    variant="flat"
                    color="error"
                    block
                    :loading="resetBtnLoading"
                    @click="resetSelectedProduct"
                    >Confirm</v-btn
                  >
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { getFunctions, httpsCallable } from "firebase/functions";
import { ref, onMounted, computed } from "vue";
import { useUserStore } from "@/store/user";
const userStore = useUserStore();
import { useDateHelpers } from "@/composables/dateHelpers";
const { easyToReadDate } = useDateHelpers();
import { usePaymentStore } from "@/store/payment";
const paymentStore = usePaymentStore();
import {
  doc,
  setDoc,
  collection,
  writeBatch,
  getDocs,
  query,
  where,
} from "firebase/firestore";
// fake loading
const loading = ref(true);
onMounted(() => {
  setTimeout(() => {
    loading.value = false;
  }, 1000);
});

// modal
const openConfirmModal = ref(false);
const resetBtnLoading = ref(false);

// delete all progress
import { useRouter } from "vue-router";
const router = useRouter();
import { useAlertStore } from "@/store/alert";
const alertStore = useAlertStore();
import { useFirestore, useCurrentUser } from "vuefire";
const currentUser = useCurrentUser();
const db = useFirestore();
const resetableProducts = {
  "duke-elder-question-bank": "Duke Elder Question Bank",
  "part-1-question-bank": "Part 1 Question Bank",
  "part-2-question-bank": "Part 2 Written Question Bank",
  "part-2-video-bank": "Part 2 Oral Question Bank",
  "interview-course": "ST1 Interview Bank",
  // "flashcard-de": "Duke-Elder Flashcards",
  // "flashcard-mla": "MLA Flashcards",
};

const selectedProduct = ref(null);
const selectedProductName = computed(() =>
  selectedProduct.value ? resetableProducts[selectedProduct.value] : "",
);

const openResetModal = (productId) => {
  selectedProduct.value = productId;
  openConfirmModal.value = true;
};

const resetSelectedProduct = async () => {
  resetBtnLoading.value = true;
  try {
    const reset = httpsCallable(
      getFunctions(undefined, "europe-west2"),
      "resetBankProgress",
    );
    await reset({
      bank:
        selectedProduct.value === "interview-course"
          ? "interview-bank"
          : selectedProduct.value,
    });

    alertStore.showAlert(
      `Your progress for ${
        resetableProducts[selectedProduct.value]
      } has been successfully reset!`,
      "success",
    );
    router.push("/");
  } catch (error) {
    alertStore.showAlert(
      "An error occurred resetting your progress. Please try again.",
      "error",
    );
    console.error("An error occurred:", error);
  }
  openConfirmModal.value = false;
  resetBtnLoading.value = false;
  selectedProduct.value = null;
};

// Add to imports section
const productNames = {
  "duke-elder-question-bank": "Duke-Elder Question Bank",
  "interview-bank": "ST1 Interview Bank",
  "st1-portfolio": "ST1 AI Portfolio",
  "part-1-question-bank": "Part 1 Question Bank",
  "part-2-question-bank": "Part 2 Written Question Bank",
  "part-2-video-bank": "Part 2 Oral Question Bank",
  "flashcard-de": "Duke-Elder Flashcards",
  "flashcard-mla": "MLA Flashcards",
  "flashcard-frcophth-part2": "FRCOphth Part 2 Flashcards",
  pub2: "Pub2 Research Programme",
};

// Add computed property to check if user has any subscription
const hasAnySubscription = computed(() =>
  Object.keys(productNames).some((id) => paymentStore.isSubscribed?.[id]?.val),
);
</script>

<style>
.nice-grad-background {
  background: linear-gradient(
    to right,
    rgb(236, 72, 153),
    rgb(239, 68, 68),
    rgb(234, 179, 8)
  ) !important;
}
</style>
